import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { MiniGameSession, MiniGameStudent, SpinWheelState } from '../../../interfaces/mini-game.interface';
import { GameQuestion } from '../../../interfaces/question.interface';
import { GlobalService } from 'src/app/_services/global.service';
import { GameAudioService } from '../../../_services/game-audio.service';

type SpinWheelPhase = 'ready' | 'spinning' | 'student-picked' | 'question' | 'result' | 'missed';

@Component({
  selector: 'app-spin-wheel-game',
  templateUrl: './spin-wheel-game.component.html',
  styleUrls: ['./spin-wheel-game.component.scss']
})
export class SpinWheelGameComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;
  currentQuestion: GameQuestion | null = null;
  wheelStudents: MiniGameStudent[] = [];
  selectedStudent: MiniGameStudent | null = null;
  selectedOption: 'A' | 'B' | 'C' | 'D' | null = null;
  internalPhase: SpinWheelPhase = 'ready';

  lastResult: { isCorrect: boolean; xpEarned: number } | null = null;
  timerValue = 0;
  private resultAudio: HTMLAudioElement | null = null;
  private pendingTimeouts: any[] = [];
  private destroyed = false;
  showInfo = false;

  constructor(
    private miniGameService: MiniGameService,
    private globalService: GlobalService,
    private gameAudioService: GameAudioService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.session = session;
        if (session) {
          this.wheelStudents = session.students;
          this.currentQuestion = session.questions[session.currentQuestionIndex] || null;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => this.lastResult = result);

    this.miniGameService.timerValue$
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => this.timerValue = val);

    this.miniGameService.timerExpired$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.internalPhase === 'question') {
          this.onTimerExpired();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.gameAudioService.stop(this.resultAudio);
    this.gameAudioService.stopCountdownAudio();
    this.destroy$.next();
    this.destroy$.complete();
    this.miniGameService.stopTimer();
  }

  private safeTimeout(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      if (!this.destroyed) fn();
    }, ms);
    this.pendingTimeouts.push(id);
  }

  onStudentSelected(student: MiniGameStudent): void {
    this.selectedStudent = student;
    this.internalPhase = 'student-picked';
    this.miniGameService.setCurrentStudent(student.id);

    // Show student spotlight for 3.5s then show question
    this.safeTimeout(() => {
      this.internalPhase = 'question';
      this.selectedOption = null;
      const timeLimit = this.session?.config.timePerQuestion || 30;
      this.miniGameService.startTimer(timeLimit);
      this.gameAudioService.startCountdownAudio(this.miniGameService.timerValue$);
    }, 3500);
  }

  onSpin(): void {
    this.internalPhase = 'spinning';
  }

  selectOption(optionId: 'A' | 'B' | 'C' | 'D'): void {
    this.selectedOption = optionId;
  }

  onTimerExpired(): void {
    this.gameAudioService.stopCountdownAudio();
    // Submit null answer (counts as missed/wrong)
    this.miniGameService.submitAnswer(null, {
      studentId: this.selectedStudent?.id
    });
    this.playResultSound(false);
    this.internalPhase = 'missed';

    // Show missed state for 2s then go back to wheel
    this.safeTimeout(() => {
      this.miniGameService.nextQuestion();
      this.internalPhase = 'ready';
      this.selectedOption = null;
      this.selectedStudent = null;
      if (this.session) {
        this.currentQuestion = this.session.questions[this.session.currentQuestionIndex] || null;
      }
    }, 2000);
  }

  submitAnswer(): void {
    if (!this.selectedOption) return;
    this.gameAudioService.stopCountdownAudio();
    this.miniGameService.submitAnswer(this.selectedOption, {
      studentId: this.selectedStudent?.id
    });
    this.playResultSound(this.lastResult?.isCorrect ?? false);
    this.internalPhase = 'result';

    // Show result for 2s then advance
    this.safeTimeout(() => {
      this.miniGameService.nextQuestion();
      this.internalPhase = 'ready';
      this.selectedOption = null;
      this.selectedStudent = null;
      if (this.session) {
        this.currentQuestion = this.session.questions[this.session.currentQuestionIndex] || null;
      }
    }, 2000);
  }

  private playResultSound(correct: boolean): void {
    this.gameAudioService.stop(this.resultAudio);
    const file = correct ? 'correctSoundEffect.mp3' : 'buzzerSoundEffect.mp3';
    this.resultAudio = this.gameAudioService.play(`assets/game-mode/sound-effects/${file}`);
  }

  getQuestionNum(): number {
    return (this.session?.currentQuestionIndex || 0) + 1;
  }

  getTotalQuestions(): number {
    return this.session?.questions.length || 7;
  }

  quitGame(): void {
    this.miniGameService.finishMiniGame();
  }

  getStudentName(): string {
    if (!this.selectedStudent) return '';
    return this.currLang === 'ar'
      ? this.selectedStudent.name.ar
      : this.selectedStudent.name.en;
  }
}
